import { cn } from '../utils/cn';
import { formatCurrency } from '../utils/format';

export interface PriceDisplayProps {
  amount: number;
  currency?: string;
  label?: string;
  sublabel?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'danger';
  showCurrency?: boolean;
  perUnit?: string;
  className?: string;
}

export function PriceDisplay({
  amount,
  currency = 'CAD',
  label,
  sublabel,
  size = 'md',
  variant = 'default',
  showCurrency = true,
  perUnit,
  className,
}: PriceDisplayProps) {
  const sizes = {
    sm: {
      amount: 'text-lg',
      label: 'text-xs',
      sublabel: 'text-xs',
    },
    md: {
      amount: 'text-2xl',
      label: 'text-sm',
      sublabel: 'text-sm',
    },
    lg: {
      amount: 'text-4xl',
      label: 'text-base',
      sublabel: 'text-base',
    },
  };

  const variants = {
    default: 'text-gray-900',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    danger: 'text-red-600',
  };

  const s = sizes[size];

  return (
    <div className={cn('', className)}>
      {label && (
        <p className={cn('text-gray-500 mb-1', s.label)}>{label}</p>
      )}
      <div className="flex items-baseline gap-1">
        <span className={cn('font-bold', s.amount, variants[variant])}>
          {formatCurrency(amount, currency)}
        </span>
        {perUnit && (
          <span className={cn('text-gray-500', s.sublabel)}>
            / {perUnit}
          </span>
        )}
      </div>
      {sublabel && (
        <p className={cn('text-gray-400 mt-0.5', s.sublabel)}>{sublabel}</p>
      )}
      {showCurrency && currency !== 'CAD' && (
        <p className={cn('text-gray-400', s.sublabel)}>
          ({currency})
        </p>
      )}
    </div>
  );
}

// Comparison component for feasibility studies
export interface PriceComparisonProps {
  prices: Array<{
    label: string;
    amount: number;
    isRecommended?: boolean;
  }>;
  currency?: string;
  className?: string;
}

export function PriceComparison({
  prices,
  currency = 'CAD',
  className,
}: PriceComparisonProps) {
  const minPrice = Math.min(...prices.map((p) => p.amount));

  return (
    <div className={cn('space-y-3', className)}>
      {prices.map((price, index) => {
        const savings = price.amount - minPrice;
        const isLowest = price.amount === minPrice;

        return (
          <div
            key={index}
            className={cn(
              'p-3 rounded-lg border',
              price.isRecommended
                ? 'border-green-300 bg-green-50'
                : 'border-gray-200 bg-white'
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{price.label}</span>
                {price.isRecommended && (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                    Recommended
                  </span>
                )}
                {isLowest && !price.isRecommended && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                    Lowest
                  </span>
                )}
              </div>
              <span className="font-bold text-gray-900">
                {formatCurrency(price.amount, currency)}
              </span>
            </div>
            {savings > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                +{formatCurrency(savings, currency)} vs lowest
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
